/**
 * Wearable Settings Page
 * Admin configuration for wearable OAuth providers
 */
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Watch, Settings, Shield, CheckCircle, XCircle, RefreshCw,
  Eye, EyeOff, Copy, ExternalLink, AlertTriangle, Info,
  Heart, Activity, Moon, Zap
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Wearable provider configurations
const PROVIDERS = {
  fitbit: {
    name: 'Fitbit',
    logo: '💚',
    color: 'bg-teal-500',
    devPortal: 'https://dev.fitbit.com/apps',
    scopes: ['activity', 'heartrate', 'sleep', 'profile'],
    oauthType: 'OAuth 2.0',
    callbackPath: '/api/biometric/wearables/oauth/fitbit/callback'
  },
  google_fit: {
    name: 'Google Fit',
    logo: '🔴',
    color: 'bg-red-500',
    devPortal: 'https://console.cloud.google.com/apis/credentials',
    scopes: ['fitness.activity.read', 'fitness.heart_rate.read', 'fitness.sleep.read'],
    oauthType: 'OAuth 2.0',
    callbackPath: '/api/biometric/wearables/oauth/google_fit/callback'
  },
  garmin: {
    name: 'Garmin Connect',
    logo: '🔵',
    color: 'bg-blue-600',
    devPortal: 'https://developer.garmin.com/gc-developer-program/',
    scopes: ['heartrate', 'sleep', 'stress', 'activity'],
    oauthType: 'OAuth 1.0a',
    callbackPath: '/api/biometric/wearables/oauth/garmin/callback'
  },
  apple_health: {
    name: 'Apple Health',
    logo: '🍎',
    color: 'bg-gray-800',
    devPortal: 'https://developer.apple.com/documentation/healthkit',
    scopes: ['HKQuantityTypeIdentifierHeartRate', 'HKCategoryTypeIdentifierSleepAnalysis'],
    oauthType: 'HealthKit (iOS)',
    callbackPath: null,
    note: 'Requires native iOS app integration'
  }
};

export default function WearableSettingsPage() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('providers');
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [formData, setFormData] = useState({ client_id: '', client_secret: '' });
  const [showSecrets, setShowSecrets] = useState({});
  const [testResults, setTestResults] = useState({});

  const token = localStorage.getItem('token');
  const baseUrl = API_URL?.replace('/api', '') || window.location.origin;

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const res = await fetch(`${API_URL}/api/biometric/wearables/admin/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setConfig(data.providers || {});
      }
    } catch (error) {
      console.error('Failed to fetch config:', error);
      toast.error('Failed to load wearable configuration');
    } finally {
      setLoading(false);
    }
  };

  const openConfigDialog = (providerId) => {
    setSelectedProvider(providerId);
    const existing = config[providerId] || {};
    setFormData({
      client_id: existing.client_id || '',
      client_secret: '' // Never pre-fill secrets
    });
    setShowConfigDialog(true);
  };

  const saveProviderConfig = async () => {
    if (!selectedProvider || !formData.client_id) {
      toast.error('Please enter Client ID');
      return;
    }

    setSaving(true);
    try {
      const params = new URLSearchParams({
        provider: selectedProvider,
        client_id: formData.client_id,
        client_secret: formData.client_secret || 'unchanged'
      });

      const res = await fetch(`${API_URL}/api/biometric/wearables/admin/configure?${params}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success(`${PROVIDERS[selectedProvider]?.name} configured successfully`);
        setShowConfigDialog(false);
        fetchConfig();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to save configuration');
      }
    } catch (error) {
      console.error('Save failed:', error);
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async (providerId) => {
    setTestResults(prev => ({ ...prev, [providerId]: 'testing' }));
    
    try {
      // For now, we just verify the config exists
      const providerConfig = config[providerId];
      if (providerConfig?.client_id) {
        // Simulate a brief test
        await new Promise(resolve => setTimeout(resolve, 1000));
        setTestResults(prev => ({ ...prev, [providerId]: 'success' }));
        toast.success(`${PROVIDERS[providerId]?.name} configuration verified`);
      } else {
        setTestResults(prev => ({ ...prev, [providerId]: 'error' }));
        toast.error('Provider not configured');
      }
    } catch (error) {
      setTestResults(prev => ({ ...prev, [providerId]: 'error' }));
      toast.error('Connection test failed');
    }
  };

  const copyCallback = (providerId) => {
    const callbackUrl = `${baseUrl}${PROVIDERS[providerId].callbackPath}`;
    navigator.clipboard.writeText(callbackUrl);
    toast.success('Callback URL copied to clipboard');
  };

  const getStatusBadge = (providerId) => {
    const providerConfig = config[providerId];
    if (!providerConfig?.client_id) {
      return <Badge variant="outline" className="text-gray-500">Not Configured</Badge>;
    }
    return <Badge className="bg-green-500">Configured</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-sky-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="wearable-settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Watch className="h-6 w-6 text-sky-500" />
            Wearable Device Settings
          </h1>
          <p className="text-gray-400 mt-1">
            Configure OAuth credentials for wearable device integrations
          </p>
        </div>
        <Button variant="outline" onClick={fetchConfig}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Info Card */}
      <Card className="bg-sky-900/30 border-sky-700">
        <CardContent className="pt-4">
          <div className="flex gap-3">
            <Info className="h-5 w-5 text-sky-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sky-100 font-medium">How to Configure Wearable Integrations</p>
              <ol className="text-sm text-sky-200/80 mt-2 space-y-1 list-decimal list-inside">
                <li>Create a developer account at the provider's developer portal</li>
                <li>Register a new application and obtain OAuth credentials</li>
                <li>Set the callback URL to the value shown below for each provider</li>
                <li>Enter the Client ID and Client Secret in the configuration form</li>
              </ol>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="providers" data-testid="tab-providers">
            <Watch className="h-4 w-4 mr-2" />
            Providers
          </TabsTrigger>
          <TabsTrigger value="usage" data-testid="tab-usage">
            <Activity className="h-4 w-4 mr-2" />
            Usage Stats
          </TabsTrigger>
        </TabsList>

        {/* Providers Tab */}
        <TabsContent value="providers" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(PROVIDERS).map(([id, provider]) => (
              <Card key={id} className="bg-slate-800/50 border-slate-700">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-lg ${provider.color} flex items-center justify-center text-2xl`}>
                        {provider.logo}
                      </div>
                      <div>
                        <CardTitle className="text-lg">{provider.name}</CardTitle>
                        <CardDescription>{provider.oauthType}</CardDescription>
                      </div>
                    </div>
                    {getStatusBadge(id)}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Scopes */}
                  <div>
                    <Label className="text-xs text-gray-500">Required Scopes</Label>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {provider.scopes.map(scope => (
                        <Badge key={scope} variant="outline" className="text-xs">
                          {scope}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  {/* Callback URL */}
                  {provider.callbackPath && (
                    <div>
                      <Label className="text-xs text-gray-500">Callback URL</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <code className="flex-1 text-xs bg-slate-900 px-2 py-1 rounded truncate">
                          {baseUrl}{provider.callbackPath}
                        </code>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0"
                          onClick={() => copyCallback(id)}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Note for Apple Health */}
                  {provider.note && (
                    <div className="bg-yellow-900/30 border border-yellow-700 rounded p-2">
                      <p className="text-xs text-yellow-300 flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" />
                        {provider.note}
                      </p>
                    </div>
                  )}

                  {/* Config Status */}
                  {config[id]?.client_id && (
                    <div className="bg-slate-900/50 rounded p-2">
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-gray-500">Client ID:</span>
                        <code className="text-green-400">{config[id].client_id.slice(0, 20)}...</code>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-gray-500">Secret:</span>
                        <span className="text-green-400">••••••••••••</span>
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2 pt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => openConfigDialog(id)}
                      disabled={!provider.callbackPath}
                    >
                      <Settings className="h-4 w-4 mr-1" />
                      Configure
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => testConnection(id)}
                      disabled={!config[id]?.client_id || testResults[id] === 'testing'}
                    >
                      {testResults[id] === 'testing' ? (
                        <RefreshCw className="h-4 w-4 animate-spin" />
                      ) : testResults[id] === 'success' ? (
                        <CheckCircle className="h-4 w-4 text-green-500" />
                      ) : testResults[id] === 'error' ? (
                        <XCircle className="h-4 w-4 text-red-500" />
                      ) : (
                        <Zap className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      asChild
                    >
                      <a href={provider.devPortal} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Usage Stats Tab */}
        <TabsContent value="usage">
          <Card>
            <CardHeader>
              <CardTitle>Wearable Usage Statistics</CardTitle>
              <CardDescription>Overview of wearable device connections and data syncs</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Watch className="h-5 w-5 text-sky-500" />
                    <span className="text-gray-400">Connected Devices</span>
                  </div>
                  <p className="text-2xl font-bold text-white">0</p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Heart className="h-5 w-5 text-red-500" />
                    <span className="text-gray-400">Heart Rate Records</span>
                  </div>
                  <p className="text-2xl font-bold text-white">0</p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Moon className="h-5 w-5 text-purple-500" />
                    <span className="text-gray-400">Sleep Records</span>
                  </div>
                  <p className="text-2xl font-bold text-white">0</p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Activity className="h-5 w-5 text-green-500" />
                    <span className="text-gray-400">Last Sync</span>
                  </div>
                  <p className="text-lg font-medium text-gray-400">Never</p>
                </div>
              </div>

              <div className="mt-6 text-center text-gray-500 py-8">
                <Watch className="h-12 w-12 mx-auto mb-2 opacity-30" />
                <p>No wearable devices connected yet</p>
                <p className="text-sm">Configure providers above, then users can connect their devices</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Configure Provider Dialog */}
      <Dialog open={showConfigDialog} onOpenChange={setShowConfigDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedProvider && (
                <>
                  <span className="text-2xl">{PROVIDERS[selectedProvider]?.logo}</span>
                  Configure {PROVIDERS[selectedProvider]?.name}
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              Enter the OAuth credentials from the {selectedProvider && PROVIDERS[selectedProvider]?.name} developer portal
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Developer Portal Link */}
            {selectedProvider && (
              <div className="bg-slate-800/50 rounded p-3">
                <p className="text-sm text-gray-400 mb-2">Get credentials from:</p>
                <a
                  href={PROVIDERS[selectedProvider]?.devPortal}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sky-400 hover:text-sky-300 text-sm flex items-center gap-1"
                >
                  {PROVIDERS[selectedProvider]?.devPortal}
                  <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            )}

            {/* Callback URL */}
            {selectedProvider && PROVIDERS[selectedProvider]?.callbackPath && (
              <div>
                <Label>Callback URL (set this in the developer portal)</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    value={`${baseUrl}${PROVIDERS[selectedProvider]?.callbackPath}`}
                    readOnly
                    className="text-xs"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyCallback(selectedProvider)}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}

            {/* Client ID */}
            <div>
              <Label htmlFor="client_id">Client ID *</Label>
              <Input
                id="client_id"
                value={formData.client_id}
                onChange={(e) => setFormData(prev => ({ ...prev, client_id: e.target.value }))}
                placeholder="Enter Client ID"
                className="mt-1"
              />
            </div>

            {/* Client Secret */}
            <div>
              <Label htmlFor="client_secret">
                Client Secret {config[selectedProvider]?.client_id ? '(leave blank to keep current)' : '*'}
              </Label>
              <div className="relative mt-1">
                <Input
                  id="client_secret"
                  type={showSecrets[selectedProvider] ? 'text' : 'password'}
                  value={formData.client_secret}
                  onChange={(e) => setFormData(prev => ({ ...prev, client_secret: e.target.value }))}
                  placeholder={config[selectedProvider]?.client_id ? '••••••••••••' : 'Enter Client Secret'}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                  onClick={() => setShowSecrets(prev => ({ ...prev, [selectedProvider]: !prev[selectedProvider] }))}
                >
                  {showSecrets[selectedProvider] ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfigDialog(false)}>
              Cancel
            </Button>
            <Button onClick={saveProviderConfig} disabled={saving}>
              {saving ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Configuration'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
